import React, { useState, useRef, useEffect } from "react";
import classes from "./AddDepartmentModal.module.css";
import { FiX, FiPlus, FiCheckCircle, FiChevronDown, FiCheck } from "react-icons/fi";

// --- CUSTOM SEARCHABLE SELECT COMPONENT (Built-in) ---
const SearchableSelect = ({ options, placeholder, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = options.filter((opt) =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={classes.searchWrapper} ref={wrapperRef}>
      <div className={classes.searchInputArea} onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          placeholder={placeholder}
          value={value ? value : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          className={classes.searchInput}
        />
        <FiChevronDown
          className={`${classes.searchChevron} ${isOpen ? classes.searchChevronOpen : ""}`}
        />
      </div>

      {isOpen && (
        <div className={classes.searchDropdown}>
          {filtered.length > 0 ? (
            filtered.map((opt) => (
              <div
                key={opt}
                className={classes.searchOption}
                onClick={() => {
                  onChange(opt);
                  setSearchTerm(opt);
                  setIsOpen(false);
                }}
              >
                <span>{opt}</span>
                {value === opt && <FiCheck className={classes.checkIcon} />}
              </div>
            ))
          ) : (
            <div className={classes.noOptions}>No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

// --- MAIN MODAL ---
const AddDepartmentModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    departmentName: "",
    departmentCode: "",
    departmentType: "",
    description: "",
    departmentHead: "",
    deputyHead: "",
  });

  const [errors, setErrors] = useState({});

  // Department Types
  const departmentTypes = [
    "Clinical / Medical",
    "Surgical",
    "Emergency & Acute Care",
    "Maternal & Women's Health",
    "Paediatric & Neonatal",
    "Diagnostic Services",
    "Oncology / Cancer Care",
    "Mental & Behavioural Health",
    "Rehabilitation",
    "Pharmacy",
    "Dental & Oral Health",
    "Outpatient Services",
    "Inpatient Services",
    "Nursing Services",
    "Public / Community Health",
    "Infection Prevention & Control",
    "Medical Records & Health Information",
    "Administration",
    "Support Services",
    "Information Technology / Digital Health",
    "Research & Education",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.departmentName) newErrors.departmentName = "Department Name is required.";
    if (!formData.departmentCode) newErrors.departmentCode = "Department Code is required.";
    if (!formData.departmentType) newErrors.departmentType = "Department Type is required.";
    if (!formData.departmentHead) newErrors.departmentHead = "Department Head is required.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    console.log("Department Data:", formData);
    alert("Department Saved Successfully!");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContainer}>
        {/* Header */}
        <div className={classes.modalHeader}>
          <div className={classes.headerTitle}>
            <div className={classes.modalIcon}><FiPlus /></div>
            <div>
              <h2 className={classes.modalTitle}>ADD NEW DEPARTMENT</h2>
              <p className={classes.modalSubtitle}>Fill in the department details below</p>
            </div>
          </div>
          <button className={classes.closeBtn} onClick={onClose}><FiX /></button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className={classes.modalBody}>

            {/* Department Information */}
            <div className={classes.section}>
              <h3 className={classes.sectionTitle}>Department Information</h3>

              <div className={classes.row2Col}>
                <div className={classes.inputGroup}>
                  <label>Department Name <span className={classes.required}>*</span></label>
                  <input
                    type="text"
                    name="departmentName"
                    placeholder="Enter department name"
                    className={`${classes.formInput} ${errors.departmentName ? classes.inputError : ''}`}
                    value={formData.departmentName}
                    onChange={handleChange}
                  />
                  {errors.departmentName && <span className={classes.errorText}>{errors.departmentName}</span>}
                </div>

                <div className={classes.inputGroup}>
                  <label>Department Code <span className={classes.required}>*</span></label>
                  <input
                    type="text"
                    name="departmentCode"
                    placeholder="Enter department code"
                    className={`${classes.formInput} ${errors.departmentCode ? classes.inputError : ''}`}
                    value={formData.departmentCode}
                    onChange={handleChange}
                  />
                  {errors.departmentCode && <span className={classes.errorText}>{errors.departmentCode}</span>}
                </div>
              </div>

              {/* --- SEARCHABLE DEPARTMENT TYPE --- */}
              <div className={classes.inputGroup}>
                <label>Department Type <span className={classes.required}>*</span></label>
                <SearchableSelect
                  options={departmentTypes}
                  placeholder="Search and select department type..."
                  value={formData.departmentType}
                  onChange={(val) => {
                    setFormData((prev) => ({ ...prev, departmentType: val }));
                    if (errors.departmentType) {
                      setErrors((prev) => ({ ...prev, departmentType: null }));
                    }
                  }}
                />
                {errors.departmentType && <span className={classes.errorText}>{errors.departmentType}</span>}
              </div>

            </div>

            {/* Department Leadership */}
            {/* <div className={classes.section}>
              <h3 className={classes.sectionTitle}>Department Leadership</h3>
              <div className={classes.row2Col}>
                <div className={classes.inputGroup}>
                  <label>Department Head <span className={classes.required}>*</span></label>
                  <select 
                    name="departmentHead"
                    className={`${classes.formSelect} ${errors.departmentHead ? classes.inputError : ''}`}
                    value={formData.departmentHead}
                    onChange={handleChange}
                  >
                    <option value="">Select department head</option>
                    <option>Dr. John Adewale</option>
                    <option>Dr. Mary Johnson</option>
                    <option>Dr. Sarah Bello</option>
                  </select>
                  {errors.departmentHead && <span className={classes.errorText}>{errors.departmentHead}</span>}
                </div>
                
                <div className={classes.inputGroup}>
                  <label>Deputy / Assistant Head</label>
                  <select 
                    name="deputyHead"
                    className={classes.formSelect}
                    value={formData.deputyHead}
                    onChange={handleChange}
                  >
                    <option value="">Select deputy</option>
                    <option>Dr. Jane Williams</option>
                    <option>Mrs. Grace Adebayo</option>
                  </select>
                </div>
              </div>
            </div> */}

          </div>

          {/* Footer */}
          <div className={classes.modalFooter}>
            <button type="button" className={classes.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" className={classes.saveBtn}><FiCheckCircle /> Save Department</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDepartmentModal;